import React, { useState, useEffect, useContext, useMemo, useLayoutEffect, useRef } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { Row, Col, Button, ButtonGroup, Spinner, Input } from "reactstrap";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";
import Header from "../Header/Header";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5locales_es_ES from "@amcharts/amcharts5/locales/es_ES";

moment.locale("es");

// Dias y meses en español, pero numeros con el formato de México (1,234.56)
const LOCALE_MX = { ...am5locales_es_ES, _decimalSeparator: ".", _thousandSeparator: "," };

const fmt = (n) =>
  "$" + parseFloat(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtN = (n) =>
  parseFloat(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Colores base
const PALETTE = ["#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6","#0ea5e9","#ec4899","#14b8a6","#f97316","#6366f1"];
const PALETTE2 = ["#10b981","#3b82f6","#f59e0b","#8b5cf6","#ef4444","#0ea5e9","#ec4899","#14b8a6"];

// ── Número animado ──────────────────────────────────────────────
function AnimatedNumber({ target, prefix = "", suffix = "" }) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);
  useEffect(() => {
    const start = Date.now();
    const duration = 900;
    const from = 0;
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + (target - from) * ease);
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target]);
  return (
    <span>
      {prefix}
      {parseFloat(display).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      {suffix}
    </span>
  );
}

// ── Ring de progreso SVG ────────────────────────────────────────
function Ring({ pct, color, size = 70, stroke = 7 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke={color} strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${circ * Math.min(pct, 100) / 100} ${circ}`}
        style={{ transition: "stroke-dasharray 1.1s cubic-bezier(.4,2,.6,1)" }}
      />
    </svg>
  );
}

// ── Barra horizontal ────────────────────────────────────────────
function BarRow({ label, value, pct, color, sub }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
        <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text-primary)", maxWidth: "64%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}
        </span>
        <span style={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>{value}</span>
      </div>
      <div style={{ background: "#f1f5f9", borderRadius: 6, height: 8, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${Math.max(2, pct)}%`, background: color, borderRadius: 6, transition: "width 1s cubic-bezier(.4,2,.6,1)" }} />
      </div>
      {sub && <div style={{ fontSize: "0.68rem", color: "var(--text-secondary)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Tarjeta con header ──────────────────────────────────────────
function DCard({ icon, iconColor, title, children, minH }) {
  return (
    <div style={{ background: "#fff", borderRadius: "var(--radius)", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", height: "100%", minHeight: minH, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "12px 16px", fontWeight: 700, fontSize: "0.83rem", color: "var(--text-primary)", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
        <i className={icon} style={{ color: iconColor, fontSize: "0.9rem" }}></i>
        {title}
      </div>
      <div style={{ padding: "14px 16px", flex: 1 }}>{children}</div>
    </div>
  );
}

// ── Badge de ranking ────────────────────────────────────────────
function RankBadge({ n }) {
  const bg = n === 1 ? "#f59e0b" : n === 2 ? "#94a3b8" : n === 3 ? "#cd7c2f" : "#e2e8f0";
  const color = n <= 3 ? "#fff" : "var(--text-secondary)";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "50%", background: bg, color, fontSize: "0.62rem", fontWeight: 700 }}>
      {n}
    </span>
  );
}

function EmptyState() {
  return (
    <div style={{ textAlign: "center", padding: "28px 0", color: "var(--text-secondary)" }}>
      <i className="fas fa-chart-bar" style={{ fontSize: "2rem", opacity: 0.2 }}></i>
      <p style={{ marginTop: 8, fontSize: "0.8rem" }}>Sin datos en el período</p>
    </div>
  );
}

// ── Componente principal ────────────────────────────────────────
function HomeInicio() {
  const { user } = useContext(AuthContext);
  const URL_DASHBOARDS = process.env.REACT_APP_URL_DASHBOARDS;

  const [cargando, setCargando] = useState(true);
  const [info, setInfo] = useState([]);
  const [periodo, setPeriodo] = useState("mes");
  const [selectedFechaInicio, setSelectedFechaInicio] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [selectedFechaFin, setSelectedFechaFin]     = useState(moment().endOf("month").format("YYYY-MM-DD"));
  const [diaMetrica, setDiaMetrica] = useState("montos"); // grafica diaria: "montos" | "piezas"
  const [cliente, setCliente] = useState("");             // filtro general: "" = todos los clientes
  // Montos en venta bruta (lo que pago el comprador, como lo reportan ML/TikTok) o neta (lo que deposita la plataforma)
  const [base, setBase] = useState("bruto");
  // Permiso "Dashboard de ventas"; con un token anterior al permiso, lo ven los administradores
  const verDashboard = user.dashboard !== undefined ? !!user.dashboard : !!user.menu_admin;

  const rootDonutRef = useRef(null);
  const rootBarRef   = useRef(null);
  const rootDiaRef   = useRef(null);

  function cambiarPeriodo(p) {
    setPeriodo(p);
    const map = {
      hoy:    [moment(), moment()],
      semana: [moment().startOf("isoWeek"), moment().endOf("isoWeek")],
      mes:    [moment().startOf("month"), moment().endOf("month")],
      año:    [moment().startOf("year"),  moment().endOf("year")],
    };
    const [ini, fin] = map[p];
    setSelectedFechaInicio(ini.format("YYYY-MM-DD"));
    setSelectedFechaFin(fin.format("YYYY-MM-DD"));
  }

  useEffect(() => {
    if (!verDashboard) { setCargando(false); return; }
    const token = localStorage.getItem("app_token");
    setCargando(true);
    axios
      .get(`${URL_DASHBOARDS}Ventas/${selectedFechaInicio}/${selectedFechaFin}`, {
        headers: { Authorization: `Bearer: ${token}` },
      })
      .then((res) => setInfo(res.data || []))
      .catch(console.error)
      .finally(() => setCargando(false));
  }, [selectedFechaInicio, selectedFechaFin, verDashboard]);

  // Clientes del periodo (filtro general del Inicio)
  const clientes = useMemo(() => {
    const map = {};
    info.forEach((i) => { if (i.idCliente) map[i.idCliente] = i.Cliente || String(i.idCliente); });
    return Object.entries(map).map(([id, nombre]) => ({ id, nombre })).sort((a, b) => (a.nombre > b.nombre ? 1 : -1));
  }, [info]);

  // Sin ImporteBruto el servidor publicado es anterior: bruto = neto
  const conBruto = info.some((i) => i.ImporteBruto !== undefined);
  const baseEf = conBruto ? base : "neto";

  // Lo que alimenta KPIs y graficas: las ventas del cliente elegido o de todos. Importe = bruto o neto segun
  // el boton; Neto se conserva para la utilidad (lo que realmente entra menos el costo)
  const datos = useMemo(
    () =>
      (cliente ? info.filter((i) => String(i.idCliente) === cliente) : info).map((i) => ({
        ...i,
        Neto: parseFloat(i.Importe || 0),
        Importe: base === "bruto" && i.ImporteBruto !== undefined ? i.ImporteBruto : i.Importe,
      })),
    [info, cliente, base]
  );

  // ── Aggregaciones ───────────────────────────────────────────
  const {
    valorVenta, cantidadVenta, clientesUnicos,
    graficasAreas, graficasLineas, graficasCodigo,
    graficasMes, utilidad,
  } = useMemo(() => {
    const agrupa = (keyField, labelField) => {
      const map = {};
      datos.forEach((item) => {
        const k = item[keyField] || "Sin clasificar";
        if (!map[k]) map[k] = { label: item[labelField] || k, Importe: 0, Neto: 0, Cantidad: 0, Costo: 0 };
        map[k].Importe   += parseFloat(item.Importe  || 0);
        map[k].Neto      += parseFloat(item.Neto     || 0);
        map[k].Cantidad  += parseFloat(item.Cantidad || 0);
        map[k].Costo     += parseFloat(item.Costo    || 0);
      });
      return Object.values(map).sort((a, b) => b.Importe - a.Importe);
    };

    const codMap = {};
    datos.forEach((item) => {
      const k = item.Codigo || "?";
      if (!codMap[k]) codMap[k] = { label: k, Importe: 0, Cantidad: 0 };
      codMap[k].Importe  += parseFloat(item.Importe  || 0);
      codMap[k].Cantidad += parseFloat(item.Cantidad || 0);
    });

    // Por mes: la llave es "YYYY-MM" para ordenar por calendario (por nombre salia abril, agosto, ...)
    const mesMap = {};
    datos.forEach((item) => {
      const k = item.Fecha ? String(item.Fecha).slice(0, 7) : (item.Mes || "?");
      if (!mesMap[k]) mesMap[k] = { key: k, label: item.Mes || k, Importe: 0 };
      mesMap[k].Importe += parseFloat(item.Importe || 0);
    });

    return {
      valorVenta:       datos.reduce((s, i) => s + parseFloat(i.Importe  || 0), 0),
      cantidadVenta:    datos.reduce((s, i) => s + parseFloat(i.Cantidad || 0), 0),
      clientesUnicos:   new Set(datos.map((i) => i.idCliente)).size,
      graficasAreas:    agrupa("idArea",     "Area"),
      graficasLineas:   agrupa("idLinea",    "Linea"),
      // Utilidad = venta (neto, aunque se vea en bruto) - costo de lo vendido. Sin el campo Costo (back sin
      // publicar) no se calcula.
      utilidad: (() => {
        const venta = datos.reduce((s, i) => s + i.Neto, 0);
        const costo = datos.reduce((s, i) => s + parseFloat(i.Costo || 0), 0);
        return {
          conCosto: datos.some((i) => i.Costo !== undefined),
          venta,
          costo,
          utilidad: venta - costo,
          margen: venta > 0 ? ((venta - costo) / venta) * 100 : 0,
          // lo vendido sin costo capturado cuenta completo como utilidad
          ventaSinCosto: datos.filter((i) => !(parseFloat(i.Costo) > 0)).reduce((s, i) => s + i.Neto, 0),
          porCanal: agrupa("idArea", "Area")
            .map((a) => ({ ...a, Utilidad: a.Neto - a.Costo, Margen: a.Neto > 0 ? ((a.Neto - a.Costo) / a.Neto) * 100 : 0 }))
            .sort((a, b) => b.Utilidad - a.Utilidad),
        };
      })(),
      graficasCodigo:   Object.values(codMap).sort((a,b)=>b.Importe-a.Importe).slice(0,10),
      graficasMes:      Object.values(mesMap).sort((a,b)=> a.key>b.key?1:-1),
    };
  }, [datos]);

  // Por dia: todos los dias del periodo hasta hoy (los que no vendieron, en 0)
  const graficasDia = useMemo(() => {
    const diaMap = {};
    datos.forEach((item) => {
      const k = String(item.Fecha || "").slice(0, 10);
      if (!k) return;
      if (!diaMap[k]) diaMap[k] = { Importe: 0, Cantidad: 0 };
      diaMap[k].Importe  += parseFloat(item.Importe  || 0);
      diaMap[k].Cantidad += parseFloat(item.Cantidad || 0);
    });
    const dias = [];
    const conVenta = Object.keys(diaMap).sort();
    if (conVenta.length) {
      const hasta = moment.max(moment.min(moment(selectedFechaFin), moment()), moment(conVenta[conVenta.length - 1]));
      for (let d = moment(selectedFechaInicio); d.isSameOrBefore(hasta, "day"); d.add(1, "day")) {
        const k = d.format("YYYY-MM-DD");
        const v = diaMap[k] || { Importe: 0, Cantidad: 0 };
        dias.push({ fecha: k, dia: d.format("dddd D [de] MMMM"), Importe: v.Importe, Cantidad: v.Cantidad });
      }
    }
    return dias;
  }, [datos, selectedFechaInicio, selectedFechaFin]);

  const ticketProm = clientesUnicos > 0 ? valorVenta / clientesUnicos : 0;
  const maxLinea   = Math.max(...graficasLineas.map((a) => a.Importe), 1);
  const maxCodigo  = Math.max(...graficasCodigo.map((a) => a.Importe), 1);
  const maxUtil    = Math.max(...utilidad.porCanal.map((a) => Math.abs(a.Utilidad)), 1);
  const enPiezas   = diaMetrica === "piezas";
  const valorDia   = (d) => (enPiezas ? d.Cantidad : d.Importe);
  const fmtDia     = (n) => (enPiezas ? `${fmtN(n)} pzs` : fmt(n));
  const promDia    = graficasDia.length ? graficasDia.reduce((s, d) => s + valorDia(d), 0) / graficasDia.length : 0;
  const mejorDia   = graficasDia.reduce((m, d) => (!m || valorDia(d) > valorDia(m) ? d : m), null);

  // ── amCharts: Donut por Canal/Área ──────────────────────────
  useLayoutEffect(() => {
    if (cargando || graficasAreas.length === 0) return;

    if (rootDonutRef.current) { rootDonutRef.current.dispose(); rootDonutRef.current = null; }

    const root = am5.Root.new("chartDonut");
    rootDonutRef.current = root;
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5percent.PieChart.new(root, {
        innerRadius: am5.percent(62),
        layout: root.horizontalLayout,
        paddingRight: 20,
      })
    );

    const series = chart.series.push(
      am5percent.PieSeries.new(root, {
        valueField: "value",
        categoryField: "category",
        tooltip: am5.Tooltip.new(root, { labelText: "{category}\n${value.formatNumber('#,###')}" }),
      })
    );
    series.labels.template.set("forceHidden", true);
    series.ticks.template.set("forceHidden", true);
    series.slices.template.setAll({ cornerRadiusInnerY: 4, cornerRadiusY: 4 });

    const legend = chart.children.push(
      am5.Legend.new(root, {
        centerY: am5.percent(50),
        y: am5.percent(50),
        layout: root.verticalLayout,
        paddingLeft: 10,
      })
    );
    legend.labels.template.setAll({ fontSize: 11, fontWeight: "600" });
    legend.valueLabels.template.set("forceHidden", true);

    series.data.setAll(
      graficasAreas.slice(0, 8).map((a) => ({ value: Math.round(a.Importe), category: a.label }))
    );
    legend.data.setAll(series.dataItems);
    series.appear(1000, 100);

    return () => { root.dispose(); rootDonutRef.current = null; };
  }, [graficasAreas, cargando]);

  // ── amCharts: Columnas por Mes ──────────────────────────────
  useLayoutEffect(() => {
    if (cargando || graficasMes.length === 0) return;

    if (rootBarRef.current) { rootBarRef.current.dispose(); rootBarRef.current = null; }

    const root = am5.Root.new("chartBar");
    rootBarRef.current = root;
    root.setThemes([am5themes_Animated.new(root)]);
    root.numberFormatter.setAll({ numberFormat: "$#,###", numericFields: ["valueY"] });

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, { panX: false, panY: false, wheelX: "none", wheelY: "none", paddingLeft: 0 })
    );

    const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 20, cellStartLocation: 0.1, cellEndLocation: 0.9 });
    xRenderer.labels.template.setAll({ fontSize: 11, fill: am5.color("#64748b") });
    xRenderer.grid.template.set("visible", false);

    const yRenderer = am5xy.AxisRendererY.new(root, {});
    yRenderer.labels.template.setAll({ fontSize: 10, fill: am5.color("#64748b") });
    yRenderer.grid.template.setAll({ stroke: am5.color("#e2e8f0"), strokeWidth: 1 });

    const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
      categoryField: "category",
      renderer: xRenderer,
    }));
    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, { renderer: yRenderer }));

    const series = chart.series.push(am5xy.ColumnSeries.new(root, {
      xAxis, yAxis,
      valueYField: "value",
      categoryXField: "category",
      tooltip: am5.Tooltip.new(root, { labelText: "{categoryX}: ${valueY.formatNumber('#,###')}" }),
    }));
    series.columns.template.setAll({
      cornerRadiusTL: 5, cornerRadiusTR: 5,
      fillOpacity: 0.9,
      strokeOpacity: 0,
    });
    series.columns.template.adapters.add("fill", (fill, target) => {
      const idx = series.columns.indexOf(target);
      const colors = PALETTE.map((c) => am5.color(c));
      return colors[idx % colors.length] || fill;
    });

    const cursor = chart.set("cursor", am5xy.XYCursor.new(root, { behavior: "none" }));
    cursor.lineY.set("visible", false);

    xAxis.data.setAll(graficasMes.map((m) => ({ category: m.label })));
    series.data.setAll(graficasMes.map((m) => ({ category: m.label, value: Math.round(m.Importe) })));
    series.appear(1000);
    chart.appear(1000, 100);

    return () => { root.dispose(); rootBarRef.current = null; };
  }, [graficasMes, cargando]);

  // ── amCharts: Columnas por Día ──────────────────────────────
  useLayoutEffect(() => {
    if (cargando || graficasDia.length === 0) return;

    if (rootDiaRef.current) { rootDiaRef.current.dispose(); rootDiaRef.current = null; }

    const root = am5.Root.new("chartDia");
    rootDiaRef.current = root;
    root.locale = LOCALE_MX;
    root.setThemes([am5themes_Animated.new(root)]);
    root.numberFormatter.setAll({ numberFormat: enPiezas ? "#,###" : "$#,###", numericFields: ["valueY"] });

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, { panX: false, panY: false, wheelX: "none", wheelY: "none", paddingLeft: 0 })
    );

    const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 45, cellStartLocation: 0.12, cellEndLocation: 0.88 });
    xRenderer.labels.template.setAll({ fontSize: 11, fill: am5.color("#64748b") });
    xRenderer.grid.template.set("visible", false);

    const yRenderer = am5xy.AxisRendererY.new(root, {});
    yRenderer.labels.template.setAll({ fontSize: 10, fill: am5.color("#64748b") });
    yRenderer.grid.template.setAll({ stroke: am5.color("#e2e8f0"), strokeWidth: 1 });

    const xAxis = chart.xAxes.push(am5xy.DateAxis.new(root, {
      baseInterval: { timeUnit: "day", count: 1 },
      dateFormats: { day: "d MMM", week: "d MMM", month: "MMM" },
      periodChangeDateFormats: { day: "d MMM", week: "d MMM", month: "MMM yyyy" },
      renderer: xRenderer,
    }));
    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, { min: 0, renderer: yRenderer }));

    const series = chart.series.push(am5xy.ColumnSeries.new(root, {
      xAxis, yAxis,
      valueYField: "value",
      valueXField: "date",
      tooltip: am5.Tooltip.new(root, { labelText: "{dia}\n${importe.formatNumber('#,###.00')} · {piezas.formatNumber('#,###.##')} pzs" }),
    }));
    series.columns.template.setAll({
      cornerRadiusTL: 4, cornerRadiusTR: 4,
      fill: am5.color(enPiezas ? "#3b82f6" : "#10b981"),
      fillOpacity: 0.9,
      strokeOpacity: 0,
    });

    // Linea punteada del promedio diario
    const promItem = yAxis.makeDataItem({ value: promDia });
    yAxis.createAxisRange(promItem);
    promItem.get("grid").setAll({ stroke: am5.color("#f59e0b"), strokeOpacity: 1, strokeWidth: 1.5, strokeDasharray: [5, 4], visible: true });
    promItem.get("label").setAll({ text: "Promedio", fill: am5.color("#d97706"), fontSize: 10, fontWeight: "600", inside: true, centerY: am5.p100, centerX: am5.p0, x: 4 });

    const cursor = chart.set("cursor", am5xy.XYCursor.new(root, { behavior: "none" }));
    cursor.lineY.set("visible", false);

    series.data.setAll(graficasDia.map((d) => ({
      date: moment(d.fecha).valueOf(),
      value: Math.round((enPiezas ? d.Cantidad : d.Importe) * 100) / 100,
      importe: Math.round(d.Importe * 100) / 100,
      piezas: d.Cantidad,
      dia: d.dia,
    })));
    series.appear(1000);
    chart.appear(1000, 100);

    return () => { root.dispose(); rootDiaRef.current = null; };
  }, [graficasDia, promDia, enPiezas, cargando]);

  // Si el cliente elegido no tiene ventas en el nuevo periodo, vuelve a "Todos"
  useEffect(() => {
    if (cliente && !cargando && !clientes.some((c) => c.id === cliente)) setCliente("");
  }, [clientes, cliente, cargando]);

  const hora   = moment().hour();
  const saludo = hora < 12 ? "Buenos días" : hora < 19 ? "Buenas tardes" : "Buenas noches";

  const kpis = [
    { label: baseEf === "bruto" ? "Venta Bruta del Período" : "Venta Neta del Período", raw: valorVenta, prefix: "$", icon: "fas fa-dollar-sign", color: "#10b981", bg: "linear-gradient(135deg,#d1fae5,#a7f3d0)",     ring: 100 },
    { label: "Piezas Vendidas",     raw: cantidadVenta,   prefix: "",  icon: "fas fa-boxes",       color: "#3b82f6", bg: "linear-gradient(135deg,#dbeafe,#bfdbfe)",       ring: 80  },
    { label: "Clientes Únicos",     raw: clientesUnicos,  prefix: "",  icon: "fas fa-users",       color: "#f59e0b", bg: "linear-gradient(135deg,#fef3c7,#fde68a)",       ring: 60  },
    { label: "Ticket Promedio",     raw: ticketProm,      prefix: "$", icon: "fas fa-chart-line",  color: "#8b5cf6", bg: "linear-gradient(135deg,#ede9fe,#ddd6fe)",       ring: 70  },
    { label: "Líneas / Canales",    raw: graficasLineas.length, prefix: "", icon: "fas fa-tags", color: "#0ea5e9", bg: "linear-gradient(135deg,#e0f2fe,#bae6fd)", ring: 50  },
  ];

  // Sin permiso de dashboard: solo el saludo
  if (!verDashboard) {
    return (
      <>
        <Header />
        <div style={{ padding: "20px 24px", background: "var(--page-bg)", minHeight: "100vh" }}>
          <h4 style={{ margin: 0, fontWeight: 700, color: "var(--text-primary)", fontSize: "1.15rem" }}>
            {saludo}, {user.nombre || user.name || ""} 👋
          </h4>
          <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: "0.82rem" }}>
            {moment().format("dddd, D [de] MMMM [de] YYYY")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div style={{ padding: "20px 24px", background: "var(--page-bg)", minHeight: "100vh" }}>

        {/* ── Encabezado ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h4 style={{ margin: 0, fontWeight: 700, color: "var(--text-primary)", fontSize: "1.15rem" }}>
              {saludo}, {user.nombre || user.name || ""} 👋
            </h4>
            <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: "0.82rem" }}>
              {moment().format("dddd, D [de] MMMM [de] YYYY")}
              {!cargando && (
                <span style={{ marginLeft: 10, color: "#10b981", fontWeight: 600 }}>
                  • {fmtN(datos.length)} registros
                </span>
              )}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Input
              type="select"
              bsSize="sm"
              value={cliente}
              onChange={(e) => setCliente(e.target.value)}
              style={{ width: 230 }}
            >
              <option value="">Todos los clientes</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </Input>
            <ButtonGroup size="sm" title={conBruto ? "Bruto: lo que pagó el comprador · Neto: lo que deposita la plataforma (sin comisión, envío ni retenciones)" : "Falta publicar el servidor para ver la venta bruta"}>
              {[{ k: "bruto", l: "Bruto" }, { k: "neto", l: "Neto" }].map(({ k, l }) => (
                <Button key={k} color={baseEf === k ? "primary" : "secondary"} outline={baseEf !== k} disabled={k === "bruto" && !conBruto && !cargando} onClick={() => setBase(k)} style={{ minWidth: 62 }}>
                  {l}
                </Button>
              ))}
            </ButtonGroup>
            <ButtonGroup size="sm">
              {[{ k: "hoy", l: "Hoy" }, { k: "semana", l: "Semana" }, { k: "mes", l: "Mes" }, { k: "año", l: "Año" }].map(({ k, l }) => (
                <Button key={k} color={periodo === k ? "primary" : "secondary"} outline={periodo !== k} onClick={() => cambiarPeriodo(k)} style={{ minWidth: 62 }}>
                  {l}
                </Button>
              ))}
            </ButtonGroup>
          </div>
        </div>

        {cargando ? (
          <div style={{ textAlign: "center", padding: "90px 0" }}>
            <Spinner style={{ width: 50, height: 50, color: "var(--sidebar-accent)" }} />
            <p style={{ marginTop: 14, color: "var(--text-secondary)", fontSize: "0.85rem" }}>Cargando datos...</p>
          </div>
        ) : (
          <>
            {/* ── KPI Cards ── */}
            <Row style={{ marginBottom: 18, marginLeft: -6, marginRight: -6 }}>
              {kpis.map((kpi, i) => (
                <Col key={i} style={{ padding: "0 6px", marginBottom: 12 }} xs={6} md>
                  <div style={{
                    background: "#fff",
                    borderRadius: "var(--radius)",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    padding: "14px 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    height: "100%",
                    borderTop: `3px solid ${kpi.color}`,
                    transition: "box-shadow .2s, transform .2s",
                  }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.12)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "translateY(0)"; }}
                  >
                    <Ring pct={kpi.ring} color={kpi.color} size={56} stroke={6} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: "0.67rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--text-secondary)", marginBottom: 3 }}>
                        {kpi.label}
                      </div>
                      <div style={{ fontSize: "1.25rem", fontWeight: 800, color: kpi.color, lineHeight: 1.1 }}>
                        {kpi.prefix}<AnimatedNumber target={Math.round(kpi.raw)} />
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>

            {/* ── Fila 2: Donut Canal + Columnas Tendencia ── */}
            <Row style={{ marginBottom: 16 }}>
              <Col md={5} style={{ marginBottom: 16 }}>
                <DCard icon="fas fa-store" iconColor="#3b82f6" title="Ventas por Canal / Área" minH={280}>
                  {graficasAreas.length === 0 ? <EmptyState /> : (
                    <div id="chartDonut" style={{ width: "100%", height: 220 }} />
                  )}
                </DCard>
              </Col>
              <Col md={7} style={{ marginBottom: 16 }}>
                <DCard icon="fas fa-chart-bar" iconColor="#0ea5e9" title="Tendencia de Ventas" minH={280}>
                  {graficasMes.length === 0 ? <EmptyState /> : (
                    <div id="chartBar" style={{ width: "100%", height: 220 }} />
                  )}
                </DCard>
              </Col>
            </Row>

            {/* ── Venta por Día ── */}
            <Row style={{ marginBottom: 16 }}>
              <Col md={12} style={{ marginBottom: 16 }}>
                <DCard icon="fas fa-calendar-day" iconColor="#10b981" title="Venta por Día" minH={300}>
                  <div style={{ marginBottom: 8 }}>
                    <ButtonGroup size="sm">
                      {[{ k: "montos", l: "Montos" }, { k: "piezas", l: "Piezas" }].map(({ k, l }) => (
                        <Button key={k} color={diaMetrica === k ? "primary" : "secondary"} outline={diaMetrica !== k} onClick={() => setDiaMetrica(k)} style={{ minWidth: 70 }}>
                          {l}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </div>
                  {graficasDia.length === 0 ? <EmptyState /> : (
                    <>
                      <div style={{ display: "flex", gap: 22, flexWrap: "wrap", fontSize: "0.76rem", color: "var(--text-secondary)", marginBottom: 6 }}>
                        <span>
                          Promedio diario: <b style={{ color: "#d97706" }}>{fmtDia(promDia)}</b>
                        </span>
                        {mejorDia && valorDia(mejorDia) > 0 && (
                          <span>
                            Mejor día: <b style={{ color: enPiezas ? "#3b82f6" : "#10b981" }}>{mejorDia.dia}</b> ({fmtDia(valorDia(mejorDia))})
                          </span>
                        )}
                        <span>
                          Días con venta: <b style={{ color: "var(--text-primary)" }}>{graficasDia.filter((d) => d.Importe > 0 || d.Cantidad > 0).length}</b> de {graficasDia.length}
                        </span>
                      </div>
                      <div id="chartDia" style={{ width: "100%", height: 240 }} />
                    </>
                  )}
                </DCard>
              </Col>
            </Row>

            {/* ── Fila 3: Top Artículos + Líneas + Utilidad ── */}
            <Row style={{ marginBottom: 16 }}>
              {/* Top Artículos */}
              <Col md={5} style={{ marginBottom: 16 }}>
                <DCard icon="fas fa-trophy" iconColor="#f59e0b" title="Top 10 Artículos">
                  {graficasCodigo.length === 0 ? <EmptyState /> : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", fontSize: "0.78rem", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            {["#","Código","Piezas","Importe",""].map((h, i) => (
                              <th key={i} style={{ padding: "5px 7px", fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", color: "var(--text-secondary)", letterSpacing: "0.05em", borderBottom: "2px solid var(--border)", textAlign: i >= 2 ? "right" : "left", whiteSpace: "nowrap" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {graficasCodigo.map((a, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid #f8fafc", cursor: "default" }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                            >
                              <td style={{ padding: "6px 7px" }}><RankBadge n={i + 1} /></td>
                              <td style={{ padding: "6px 7px", fontWeight: 600, maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.label}</td>
                              <td style={{ padding: "6px 7px", textAlign: "right", color: "var(--text-secondary)" }}>{fmtN(a.Cantidad)}</td>
                              <td style={{ padding: "6px 7px", textAlign: "right", fontWeight: 700, color: "#10b981" }}>{fmt(a.Importe)}</td>
                              <td style={{ padding: "6px 7px", width: "25%" }}>
                                <div style={{ background: "#f1f5f9", borderRadius: 4, height: 5 }}>
                                  <div style={{ height: "100%", width: `${(a.Importe / maxCodigo) * 100}%`, background: PALETTE[i % PALETTE.length], borderRadius: 4, transition: "width 1s ease" }} />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </DCard>
              </Col>

              {/* Columna central: Líneas */}
              <Col md={4} style={{ marginBottom: 16 }}>
                <DCard icon="fas fa-tags" iconColor="#f59e0b" title="Ventas por Línea">
                  {graficasLineas.length === 0 ? <EmptyState /> : (
                    graficasLineas.slice(0, 9).map((a, i) => (
                      <BarRow key={i} label={a.label} value={fmt(a.Importe)}
                        pct={(a.Importe / maxLinea) * 100} color={PALETTE2[i % PALETTE2.length]}
                        sub={`${fmtN(a.Cantidad)} pzs`} />
                    ))
                  )}
                </DCard>
              </Col>

              {/* Utilidad */}
              <Col md={3} style={{ marginBottom: 16 }}>
                <DCard icon="fas fa-coins" iconColor="#10b981" title="Utilidad">
                  {!utilidad.conCosto ? (
                    <div style={{ textAlign: "center", padding: "28px 8px", color: "var(--text-secondary)", fontSize: "0.8rem" }}>
                      <i className="fas fa-server" style={{ fontSize: "1.6rem", opacity: 0.25 }}></i>
                      <p style={{ marginTop: 8 }}>Falta publicar el servidor para traer el costo de lo vendido.</p>
                    </div>
                  ) : datos.length === 0 ? <EmptyState /> : (
                    <>
                      <div style={{ textAlign: "center", paddingBottom: 10, marginBottom: 10, borderBottom: "1px solid #f1f5f9" }}>
                        <div style={{ fontSize: "1.35rem", fontWeight: 800, color: utilidad.utilidad >= 0 ? "#10b981" : "#ef4444", lineHeight: 1.1 }}>
                          {fmt(utilidad.utilidad)}
                        </div>
                        <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--text-primary)", marginTop: 2 }}>
                          Margen {utilidad.margen.toFixed(1)}%
                        </div>
                        <div style={{ fontSize: "0.7rem", color: "var(--text-secondary)", marginTop: 4 }}>
                          Venta neta {fmt(utilidad.venta)} · Costo {fmt(utilidad.costo)}
                        </div>
                      </div>
                      {utilidad.porCanal.slice(0, 6).map((a, i) => (
                        <BarRow
                          key={i}
                          label={a.label}
                          value={fmt(a.Utilidad)}
                          pct={(Math.abs(a.Utilidad) / maxUtil) * 100}
                          color={a.Utilidad >= 0 ? "#10b981" : "#ef4444"}
                          sub={`Margen ${a.Margen.toFixed(1)}% · venta neta ${fmt(a.Neto)}`}
                        />
                      ))}
                      {utilidad.ventaSinCosto > 0.5 && (
                        <div style={{ marginTop: 8, padding: "6px 8px", background: "#fff7ed", border: "1px solid #fdba74", borderRadius: 6, fontSize: "0.68rem", color: "#9a3412" }}>
                          <i className="fas fa-exclamation-triangle" style={{ marginRight: 4 }}></i>
                          {fmt(utilidad.ventaSinCosto)} de venta sin costo capturado (cuenta completo como utilidad)
                        </div>
                      )}
                    </>
                  )}
                </DCard>
              </Col>
            </Row>
          </>
        )}
      </div>
    </>
  );
}

export default HomeInicio;
